import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { FEEDBACK_REASONS, FEEDBACK_SOURCE_LABELS } from "components/Misc/ComprehendFeedback";
import { ACCURACY_TAGS } from "./NoteAccuracyPanel";
import { flattenNote } from "../../utils/noteDiff";
import NoteReviewReader, { SearchableSource, SectionInstructions, TemplateSummary, transcriptDocuments } from "./NoteReviewReader";

export const reasonLabel = (id) =>
  FEEDBACK_REASONS.find((reason) => reason.id === id)?.label || id;

export const sourceLabel = (source) => FEEDBACK_SOURCE_LABELS[source] || source || "Comprehend";

// What the rated run generated: generatedSections is null for the whole note.
const ratedRunLabel = (rating) =>
  `${sourceLabel(rating.source)} · ${rating.generatedSections?.length ? rating.generatedSections.join(", ") : "Whole note"}`;

// Postgres timestamptz text ("2026-09-28 15:14:00+00") is not a format Date
// parses; make it ISO first.
const dateLabel = (value) =>
  new Date(String(value).replace(" ", "T").replace(/([+-]\d\d)$/, "$1:00")).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });

// One entry per note: several Comprehend clicks on one visit share a note.
export const reviewableNotes = (ratings) => ratings
  .filter((rating, index) => rating.noteId
    && ratings.findIndex((other) => other.noteId === rating.noteId) === index)
  .map((rating) => ({
    noteId: rating.noteId,
    noteType: rating.capturedNoteType || rating.noteType || "Captured note",
    userId: rating.userId,
    emr: rating.capturedEmr || rating.emr,
    signedAt: rating.signedAt,
    pctChanged: rating.pctChanged,
  }));

// One thumbs down: when, who, where, what they picked and wrote, and the way
// into its captured note when there is one. Shared by the look-up modal and the
// "Latest thumbs downs" list.
export function RatingRow({ rating, onReview, apiClient }) {
  const [showNote, setShowNote] = useState(false);
  return (<>
    <article className="fb-rating">
      <div className="fb-rating-content">
        <h4 className="fb-rating-reason">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
            <path d="M17 14V2M9 18l1-4H4a2 2 0 0 1-2-2.5l2-8A2 2 0 0 1 6 2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-3l-5 8a3 3 0 0 1-3-4Z" />
          </svg>
          {rating.reasons.length ? rating.reasons.map(reasonLabel).join(" · ") : "Thumbs down — no reason selected"}
        </h4>
        {rating.comment
          ? <blockquote className="fb-rating-comment">“{rating.comment}”</blockquote>
          : <p className="fb-rating-no-comment">No written comment</p>}
        <div className="fb-rating-sections">
          <span className="fb-rating-label">Rated</span>
          <span>{ratedRunLabel(rating)}</span>
        </div>
        <div className="fb-rating-sections">
          <span className="fb-rating-label">Sections</span>
          <span>{rating.sections.length ? rating.sections.join(" · ") : "Not specified"}</span>
        </div>
      </div>
      <div className="fb-rating-context">
        <span className="fb-rating-date">{dateLabel(rating.createdAt)}</span>
        <span className="fb-rating-clinician" title={rating.userId}>{rating.userId}</span>
        <span>{rating.noteType || "Visit type not specified"} · {rating.emr}</span>
        <div className="fb-rating-actions">
          {rating.noteId
            ? <Button size="sm" outline onClick={onReview}>View note &amp; changes</Button>
            : <Button size="sm" outline onClick={() => setShowNote(true)}>View note</Button>}
        </div>
      </div>
    </article>
    {showNote && <FeedbackNotePreview rating={rating} apiClient={apiClient} onClose={() => setShowNote(false)} />}
  </>);
}

// Group flattened fields under their note section, in note order.
const noteSections = (note) => {
  const sections = [];
  flattenNote(note).forEach((field) => {
    const last = sections[sections.length - 1];
    if (last?.key === field.section) last.fields.push(field);
    else sections.push({ key: field.section, fields: [field] });
  });
  return sections;
};

function FeedbackNotePreview({ rating, apiClient, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [showTranscript, setShowTranscript] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const request = apiClient.JMPost("/v1/notes/feedback/note", { conversationId: rating.conversationId, runId: rating.runId });
    request.then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setDetail(response);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [apiClient, rating.conversationId, rating.runId, retry]);
  // The text the rated run wrote, when the panel sent a copy; older ratings only
  // have the visit's latest note, which later runs may have changed.
  const rated = Boolean(detail?.ratedNote);
  const note = rated ? detail.ratedNote : detail?.note;
  const sections = note && typeof note === "object" ? noteSections(note) : [];
  const transcript = useMemo(() => (detail ? transcriptDocuments(detail) : []), [detail]);
  return <Modal isOpen toggle={onClose} className="note-review-modal" contentClassName="note-review-modal-content" labelledBy="fb-note-preview-title" size="xl" returnFocusAfterClose>
    <ModalHeader toggle={onClose} id="fb-note-preview-title">
      <span>{rated ? "Rated note" : "Latest saved note"}</span>
      <small>{rating.userId} · {rating.noteType || "Note"} · {rating.emr}</small>
    </ModalHeader>
    <ModalBody className="note-review-body">
      {loading ? <div className="note-review-loading"><Spinner /> Opening note…</div> : error
        ? <Alert color="danger">Unable to load the note. <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>
        : <div className={`note-review-workspace note-review-no-nav ${showTranscript ? "" : "note-review-source-hidden"}`}>
          <section className="note-review-document" aria-label="Saved note">
            <div className="note-review-document-toolbar">
              <span className="note-review-label-context">{rated
                ? (rating.generatedSections?.length
                  ? "The text this run wrote. Only the sections it regenerated are shown."
                  : "The note exactly as this run wrote it.")
                : "This is the latest note saved for the visit and may differ from the rated version."}</span>
              <div className="note-review-toggles">
                <Button size="sm" outline aria-pressed={showInstructions} onClick={() => setShowInstructions(!showInstructions)}>{showInstructions ? "Hide instructions" : "Show instructions"}</Button>
                <Button size="sm" outline onClick={() => setShowTranscript(!showTranscript)}>{showTranscript ? "Hide transcript" : "Show transcript"}</Button>
              </div>
            </div>
            <div className="note-review-document-content">
              <TemplateSummary instructions={detail.instructions} />
              {sections.map((section) => <section className="note-review-section" key={section.key}>
                <h3>{section.key}</h3>
                {section.fields.map((field, index) => <div className="note-review-field" key={index}>
                  {field.field && <h4>{field.field}</h4>}
                  <div className="note-review-prose">{field.text || <span className="note-accuracy-muted">Empty</span>}</div>
                </div>)}
                {showInstructions && <SectionInstructions instructions={detail.instructions} section={section.key} />}
              </section>)}
              {!sections.length && (typeof note === "string" && note.trim()
                ? <div className="note-review-prose">{note}</div>
                : <p className="note-accuracy-empty">No saved note text is available for this visit.</p>)}
            </div>
          </section>
          {showTranscript && <aside className="note-review-source" aria-label="Transcript">
            <SearchableSource documents={transcript} />
          </aside>}
        </div>}
    </ModalBody>
  </Modal>;
}

// The ratings behind one dashboard card. A rating whose note Get All Notes
// captured and the clinician signed opens in the Note Editing reader; the
// reader is shown instead of this list (not on top of it) and returns to it.
export default function FeedbackRatingsModal({ apiClient, request, look, title, onClose }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    setReviewIndex(null);
    setError("");
    apiClient.JMPost("/v1/notes/feedback/ratings", {
      ...request,
      startDate: look.startDate || request.startDate,
      endDate: look.endDate || request.endDate,
      lookEmr: look.emr || undefined,
      section: look.section || undefined,
      reason: look.reason || undefined,
      keys: look.keys || undefined,
    }).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setResult(response);
    }).catch(() => {
      if (active) setError("Unable to load these ratings. Try again.");
    });
    return () => { active = false; };
  }, [apiClient, request, look, retry]);

  const ratings = result?.ratings || [];
  const reviewable = reviewableNotes(ratings);

  if (reviewIndex !== null && reviewable[reviewIndex]) {
    return <NoteReviewReader
      apiClient={apiClient} note={reviewable[reviewIndex]} index={reviewIndex}
      count={reviewable.length} preferredSection={look.section || undefined}
      tagLabels={ACCURACY_TAGS}
      onClose={() => setReviewIndex(null)} onNavigate={setReviewIndex}
    />;
  }

  return (
    <Modal isOpen toggle={onClose} size="lg" centered className="fb-ratings-modal" returnFocusAfterClose>
      <ModalHeader toggle={onClose}>
        <span>{title}</span>
        {result && <small className="fb-ratings-subtitle">
          {result.total.toLocaleString()} thumbs {result.total === 1 ? "down" : "downs"}
          {result.total > ratings.length ? ` · showing the latest ${ratings.length}` : ""}
          {look.note ? ` · ${look.note}` : ""}
        </small>}
      </ModalHeader>
      <ModalBody className="fb-ratings-body">
        <p className="fb-muted">Where available, review this visit’s captured AI baseline against its signed note. The baseline may come from a different Comprehend run than this rating.</p>
        {error && <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
        {!result && !error && <div className="fb-loading"><Spinner size="sm" /> Loading ratings…</div>}
        {result && !ratings.length && <p className="fb-muted">No thumbs downs match.</p>}
        {ratings.map((rating) => (
          <RatingRow key={`${rating.conversationId}:${rating.runId}`} rating={rating} apiClient={apiClient}
            onReview={() => setReviewIndex(reviewable.findIndex((note) => note.noteId === rating.noteId))} />
        ))}
      </ModalBody>
    </Modal>
  );
}
